import { LazyLoading } from "@/components/loaders/Loader/LazyLoading";
import { useAssetDetailsContext } from "@/pages/asset-details/context/assetDetailsContext";
import { upsertMarkDownText } from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import { Button, CircularProgress, Grid, Snackbar } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import MarkdownIt from "markdown-it";
import { type SyntheticEvent, useEffect, useMemo, useState } from "react";
import MdEditor from "react-markdown-editor-lite";
import "react-markdown-editor-lite/lib/index.css";
import { useSearchParams } from "react-router-dom";
import { useSearchMarkDownText } from "./hooks/useSearchMarkDownText";
import { ErrorModal } from "@dexteel/mesf-core";

const useStyles = makeStyles((theme) =>
	createStyles({
		root: {
			"& .button-type-table": {
				display: "none !important",
			},
			"& .button-type-strikethrough": {
				display: "none !important",
			},
		},
		markDownTextComponent: {
			height: "90%",
			width: "100%",
			borderRadius: "5px",
			whiteSpace: "break-spaces",
		},
		buttonContainer: {
			padding: theme.spacing(1, 0),
			minHeight: 60,
			flexShrink: 0,
		},
		buttonGrid: {
			marginBottom: theme.spacing(0.5),
		},
		editorContainer: {
			display: "flex",
			flexDirection: "column",
			height: "100%",
		},
		editorWrapper: {
			flex: 1,
			minHeight: 0,
		},
	}),
);

export const AssetTextGrid = () => {
	const [searchParams] = useSearchParams();
	const containerStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const mdParser = useMemo(() => new MarkdownIt(), []);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [updateMarkDownerror, setUpdateMarkDownError] = useState<string>("");

	const gridStyle = useMemo(
		() => ({
			height: "100%",
			width: "100%",
		}),
		[],
	);
	const [editMarkDownTextSuccess, setEditMarkDownTextSuccess] = useState(false);
	const classes = useStyles();
	const resource = searchParams.get("resource") ?? "";
	const {
		state: {
			dataMarkDownText,
			assetSearchId,
			editMarkDownTextMood,
			statisticsTable,
			workOrdersTable,
		},
		actions: { setDataMarkDownText, setEditMarkDownTextMood },
	} = useAssetDetailsContext();

	const searchMarkDownText = useSearchMarkDownText({ setIsLoading, setError });

	const handleCancel = () => {
		setEditMarkDownTextMood(false);
		searchMarkDownText();
	};

	const handleClose = (event?: SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setEditMarkDownTextSuccess(false);
	};

	const handleSaveBtn = async () => {
		setIsSubmitLoading(true);
		const res = await upsertMarkDownText(assetSearchId, dataMarkDownText);
		if (res.ok) {
			searchMarkDownText();
			setIsSubmitLoading(false);
			setEditMarkDownTextSuccess(true);
			setEditMarkDownTextMood(false);
		} else {
			setUpdateMarkDownError(res.message);
			setIsSubmitLoading(false);
		}
	};

	useEffect(() => {
		if (resource === "text") {
			searchMarkDownText();
			setEditMarkDownTextMood(false);
		}
	}, [resource, assetSearchId]);

	return (
		<>
			{isLoading ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						width: "100%",
						height: "100%",
					}}
				>
					<LazyLoading />
				</div>
			) : (
				<Grid container style={containerStyle}>
					<Grid container item md={12} xs={12} style={gridStyle}>
						{!editMarkDownTextMood ? (
							<Grid item md={12} xs={12} style={{ height: "100%" }}>
								<div className={classes.editorContainer}>
									<div className={classes.editorWrapper}>
										<pre className={classes.markDownTextComponent}>
											<MdEditor
												style={{ height: "100%", width: "100%" }}
												value={(dataMarkDownText as string) || ""}
												renderHTML={(text) => mdParser.render(text || "")}
												onChange={({ text }) => setDataMarkDownText(text)}
												config={{
													view: {
														menu: false,
														md: false,
														html: true,
													},
												}}
											/>
										</pre>
									</div>
									<Grid
										container
										justifyContent="flex-end"
										spacing={1}
										className={classes.buttonContainer}
									>
										<Grid
											item
											xs={6}
											sm={3}
											md={2}
											className={classes.buttonGrid}
										>
											<Button
												fullWidth
												variant="contained"
												color="primary"
												onClick={() => setEditMarkDownTextMood(true)}
											>
												EDIT
											</Button>
										</Grid>
									</Grid>
								</div>
							</Grid>
						) : (
							<Grid item md={12} xs={12} style={{ height: "100%" }}>
								<div className={classes.editorContainer}>
									<div className={classes.editorWrapper}>
										<MdEditor
											style={{ height: "100%", width: "100%", padding: "0" }}
											value={(dataMarkDownText as string) || ""}
											renderHTML={(text) => mdParser.render(text)}
											onChange={({ text }) => setDataMarkDownText(text)}
											config={{
												view: {
													html: false,
												},
											}}
										/>
									</div>
									<Grid
										container
										justifyContent="flex-end"
										spacing={1}
										className={classes.buttonContainer}
									>
										<Grid
											item
											xs={6}
											sm={3}
											md={2}
											className={classes.buttonGrid}
										>
											<Button
												fullWidth
												variant="contained"
												color="default"
												onClick={handleCancel}
											>
												CANCEL
											</Button>
										</Grid>
										<Grid
											item
											xs={6}
											sm={3}
											md={2}
											className={classes.buttonGrid}
										>
											<Button
												fullWidth
												startIcon={
													isSubmitLoading && <CircularProgress size="1rem" />
												}
												disabled={isSubmitLoading}
												variant="contained"
												color="primary"
												onClick={handleSaveBtn}
											>
												SAVE
											</Button>
										</Grid>
									</Grid>
								</div>
							</Grid>
						)}
					</Grid>
				</Grid>
			)}
			<Snackbar
				open={editMarkDownTextSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity="info" onClose={handleClose}>
					Text edited
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={updateMarkDownerror}
				onHide={() => setUpdateMarkDownError("")}
			/>
		</>
	);
};
